/**
 * Rex Mortis — In-game Rules of Conduct
 *
 * Plain-language guide to what gets you punished. Tone is in-character
 * (the realm's edicts) but the rules themselves are concrete and
 * enforceable. Updates here should be mirrored in the patch notes so
 * players know when rules change.
 */
import { mountShellOrStandalone } from '../components/Shell.js';

export async function renderRules(root: HTMLElement): Promise<void> {
  mountShellOrStandalone(root, (main) => {
    main.innerHTML = `
      <img class="page-banner" src="/img/banners/rules.webp" alt="" onerror="this.remove()" />
      <h1>Rules of the Realm</h1>
      <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
        The realm runs by law — break these and the realm will break you.
      </p>

      <section class="panel">
        <h2 style="margin-top: 0;">1 · One soul, one account</h2>
        <p>You may hold exactly one account. Multi-accounting — running more than one player at the same time, whether to feed crowns, win wars, or stuff leaderboards — gets every linked account banned without refund. We detect through IP, browser fingerprint, behavioural patterns, and trade graphs. Family households sharing a single device may petition for an exception through the feedback channel.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">2 · No account sharing or trading</h2>
        <p>Your account is yours. Don't sell it, don't give your password to a friend, don't share a login. Selling, gifting, or buying an account violates these rules and the Terms of Service, and ends the account immediately. We will not transfer ownership between people for any reason.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">3 · No real-money trading</h2>
        <p>Crowns, items, boons, holdings, and any other in-game value may not be traded for real money or for goods/services outside the realm. This includes buying crowns through third-party sellers, paying for boosts, or selling rare drops on auction sites. Both buyer and seller lose their accounts.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">4 · No exploits, no bots, no scripts</h2>
        <p>Automating actions — clickers, macros, headless scrapers, scripted attacks — is not allowed. If you find a bug that lets you gain crowns, items, XP, or any advantage beyond the intended mechanics, you must report it through the feedback form. Exploiting a known bug is a banning offence; reporting one in good faith earns a thanks and often a reward.</p>
        <p>The game has rate limits and anti-grief safeguards. Trying to defeat them is grounds for suspension.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">5 · Chat and community conduct</h2>
        <p>The Shoutbox, House chat, mail, and any other player-to-player communication must follow these standards:</p>
        <ul style="margin: 0; padding-left: 1.2em;">
          <li>No threats of real-world violence, doxxing, or harassment.</li>
          <li>No hateful content directed at race, religion, sex, gender identity, sexual orientation, disability, or nationality.</li>
          <li>No sexually explicit content. The realm is medieval, not pornographic.</li>
          <li>No spam, no advertising, no recruiting for other games or external services.</li>
          <li>Trash talk between rivals is fine. Targeted, sustained abuse of one person is not.</li>
        </ul>
        <p>Moderators may delete messages and suspend chat privileges at their discretion. Repeat offenders lose access permanently.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">6 · Character and house names</h2>
        <p>Names must not contain slurs, real-world hate symbols, sexual content, or impersonations of staff or other players. Names that obviously try to skirt these rules (e.g. obvious leetspeak of a banned term) will be force-renamed. If your name is taken, pick another — we do not free names from inactive accounts on request.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">7 · PvP and warfare</h2>
        <p>Attacks, plunder, mercy, cripple, and house wars are all legal play. Being defeated, robbed, or jailed by another player is part of the realm. If you don't want to be attacked, stay in the Infirmary. New arrivals are protected from PvP until they finish the tutorial.</p>
        <p>What's <em>not</em> legal: using out-of-game pressure (real-world threats, doxxing, calling someone's workplace, etc.) to influence in-game outcomes. That gets the bully banned no matter what the in-game justification is.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">8 · The merchant's market</h2>
        <p>You may list any item the merchant accepts at any price. Price-fixing rings, wash trades between accounts you control, and intentional listing manipulation count as exploits (see §4). The NPC merchant always pays a floor — anything you sell to other players is your own affair, but stay honest about it.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">9 · Reporting</h2>
        <p>To report a player or a bug, use the feedback form in Settings. Include the offender's character name, the chat channel or page where it happened, and any details you can. We act on every report, though we don't always reply.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">10 · Enforcement</h2>
        <p>Punishments range from warnings, to chat suspensions, to temporary bans, to permanent bans. We weigh severity, intent, and history. Bans are at our sole discretion. We don't refund spent boons or unspent balances on banned accounts.</p>
        <p>If you think a ban was unfair, you can appeal via the feedback form. We'll look at the evidence again. We don't argue ban decisions on chat or in public threads.</p>
      </section>

      <p class="caption" style="margin-top: var(--s-5); text-align: center;">
        Last updated 2026-05-12 · By playing, you agree to these rules and to the <a href="#/tos">Terms of Service</a> and <a href="#/privacy">Privacy Policy</a>.
      </p>
    `;
  });
}
